import { useState, useEffect } from 'react';
import { sampleReviews, StarRating, PlatformIcon, normalizeReviewData, ReviewsLoadingState, ReviewsEmptyState, VerificationIcon, getFontFamily } from './SharedComponents';
import PlatformSelectionModal from './PlatformSelectionModal';
import ReviewsModal from './ReviewsModal';
import { capitalizePlatformName } from '../../utils/platformNames';

// Helper function to check if a string is a valid image URL
const isValidImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;

    // Check if it's a data URI image
    if (url.startsWith('data:image/')) return true;

    // Check if it's a proper HTTP/HTTPS URL
    const isUrl = url.startsWith('http://') || url.startsWith('https://');
    if (!isUrl) return false;

    // Check for common image extensions
    const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i;
    if (imageExtensions.test(url)) return true;

    // Check for known image hosting services (more comprehensive list)
    const imageHosts = new RegExp('(amazonaws\\.com|cloudfront\\.net|googleapis\\.com|googleusercontent\\.com|gravatar\\.com|unsplash\\.com|imgur\\.com|cloudinary\\.com|imagekit\\.io|uploadcare\\.com|cloudflare\\.com/[^/]*/image|facebook\\.com/[^/]*/picture|graph\\.facebook\\.com|fbcdn\\.net|twimg\\.com|linkedin\\.com/[^/]*/image|bing\\.com/[^/]*/image)', 'i');
    if (imageHosts.test(url)) return true;

    // For any other URL that might be an image, we'll try to load it
    // This is more permissive but safer for user-generated content
    return true;
};

// Helper function to generate initials from name
const generateInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
};

// Enhanced sample data with verification status and platform ratings
const enhancedSampleReviews = [
    ...sampleReviews,
    {
        id: 6,
        name: 'Jennifer Smith',
        rating: 5,
        comment: 'Exceptional quality and service. The attention to detail is remarkable and the customer support team is incredibly responsive.',
        platform: 'Amazon',
        date: '3 days ago',
        avatar: 'JS',
        verified: true
    },
    {
        id: 7,
        name: 'Robert Brown',
        rating: 4,
        comment: 'Very satisfied with my purchase. Fast shipping and exactly as described.',
        platform: 'Facebook',
        date: '5 days ago',
        avatar: 'RB',
        verified: true
    },
    {
        id: 8,
        name: 'Maria Garcia',
        rating: 5,
        comment: 'Incredible experience from start to finish. Will definitely recommend to friends and family.',
        platform: 'TripAdvisor',
        date: '1 week ago',
        avatar: 'MG',
        verified: false
    },
    {
        id: 9,
        name: 'Lalo Mendoza',
        rating: 5,
        comment: 'hace como 2 meses, nos corrió de la casa mi madrastra a mi papá y a mí, andadúvimos viviendo en el carro durante 5 día...',
        platform: 'Google',
        date: '17 hours ago',
        avatar: 'LM',
        verified: true
    },
    {
        id: 10,
        name: 'Mohammed M...',
        rating: 5,
        comment: "It's worth doing the mile hike from the west side. It does get steep at some places but easily doable for most people. Makes...",
        platform: 'Google',
        date: '17 hours ago',
        avatar: 'MM',
        verified: true
    },
    {
        id: 11,
        name: 'Justin Wieringa',
        rating: 5,
        comment: 'Great service, great referral program. Highly recommend',
        platform: 'Google',
        date: '18 hours ago',
        avatar: 'JW',
        verified: true
    },
    {
        id: 12,
        name: 'TAXI24 Most',
        rating: 5,
        comment: 'Vezmeme vás kdykoliv, kamkoliv.',
        platform: 'Google',
        date: '19 hours ago',
        avatar: 'TM',
        verified: true
    }
];

// Removed unused VerificationBadge component

// Navigation Arrow Component
const NavigationArrow = ({ direction, onClick, disabled }) => {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`absolute top-1/2 transform -translate-y-1/2 ${direction === 'left' ? '-left-4' : '-right-4'
                } w-10 h-10 bg-white rounded-full shadow-lg border flex items-center justify-center transition-all duration-200 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed z-10`}
        >
            <svg
                className={`w-5 h-5 text-gray-600 ${direction === 'right' ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
        </button>
    );
};

// Compact Review Card Component matching image design
const CompactReviewCard = ({ review, previewMode = 'desktop', themeStyles = null, styleConfig = {} }) => {
    const [showFullReview, setShowFullReview] = useState(false);
    const normalizedReview = normalizeReviewData(review);

    // Extract iconUrl from original review data
    const platformIconUrl = review.platform?.iconLink;
    const isLongReview = normalizedReview.comment && normalizedReview.comment.length > (previewMode === 'mobile' ? 80 : 120);

    // Use theme styles if provided, otherwise fall back to default light theme
    const cardBg = themeStyles?.reviewBg || '#FFFFFF';
    const textColor = themeStyles?.reviewText || '#000000';
    const nameColor = themeStyles?.reviewerName || '#000000';
    const dateColor = themeStyles?.dateColor || '#666666';
    const isDark = themeStyles && (cardBg === '#374151' || cardBg.includes('gray'));

    // Get style configuration values
    const textSize = styleConfig?.review?.textSize || 16;
    const cornerRadius = styleConfig?.review?.cornerRadius || 8;
    const outlineColor = styleConfig?.review?.outlineColor || '#E5E5E5';

    return (
        <div
            className={`shadow-sm border transition-all duration-200 hover:shadow-md flex flex-col ${previewMode === 'mobile' ? 'p-4 min-h-[180px]' : 'p-6 min-h-[200px]'} ${isDark ? 'hover:border-gray-500' : 'hover:border-gray-200'}`}
            style={{
                backgroundColor: cardBg,
                borderRadius: `${cornerRadius}px`,
                borderColor: outlineColor
            }}
        >
            {/* Header Section */}
            <div className={`flex items-start space-x-3 ${previewMode === 'mobile' ? 'mb-3' : 'mb-4'}`}>
                <div className="flex-shrink-0">
                    <div className={`rounded-full relative ${previewMode === 'mobile' ? 'w-10 h-10' : 'w-12 h-12'}`}>
                        {isValidImageUrl(normalizedReview.avatar) ? (
                            <>
                                <img
                                    src={normalizedReview.avatar}
                                    alt={normalizedReview.name}
                                    className={`rounded-full object-cover ${previewMode === 'mobile' ? 'w-10 h-10' : 'w-12 h-12'}`}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextElementSibling.style.display = 'flex';
                                    }}
                                />
                                <div className={`bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold absolute top-0 left-0 hidden ${previewMode === 'mobile' ? 'w-10 h-10 text-xs' : 'w-12 h-12 text-sm'}`}>
                                    {generateInitials(normalizedReview.name)}
                                </div>
                            </>
                        ) : (
                            <div className={`bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold ${previewMode === 'mobile' ? 'w-10 h-10 text-xs' : 'w-12 h-12 text-sm'}`}>
                                {generateInitials(normalizedReview.name)}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="mb-1">
                        <div className="flex items-center space-x-1">
                            <h4
                                className={`font-semibold truncate ${previewMode === 'mobile' ? 'text-sm' : 'text-base'}`}
                                style={{ color: nameColor }}
                            >
                                {normalizedReview.name}
                            </h4>
                            {normalizedReview.isVerified && (
                                <VerificationIcon
                                    size="14"
                                    isDark={isDark}
                                    accentColor={styleConfig?.accentColor || '#4A90E2'}
                                />
                            )}
                        </div>
                    </div>
                    <div className="flex items-center space-x-1 text-xs flex-wrap" style={{ color: dateColor }}>
                        <span className="whitespace-nowrap">{normalizedReview.date}</span>
                        <span>•</span>
                        <div className="flex items-center space-x-1 flex-shrink-0">
                            <PlatformIcon
                                platform={normalizedReview.platform}
                                iconUrl={platformIconUrl}
                                className="w-3 h-3"
                            />
                            <span className="whitespace-nowrap">{normalizedReview.platform}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Rating Display */}
            <div className="mb-3">
                <StarRating rating={normalizedReview.rating} size="w-5 h-5" color={styleConfig?.rating?.starsColor || '#F1C40F'} />
            </div>

            {/* Review Content */}
            <div className="flex-1 flex flex-col">
                {normalizedReview.comment ? (
                    <>
                        <p
                            className="leading-relaxed flex-1"
                            style={{
                                color: textColor,
                                fontSize: `${textSize}px`
                            }}
                        >
                            {isLongReview && !showFullReview
                                ? `${normalizedReview.comment.substring(0, previewMode === 'mobile' ? 80 : 120)}...`
                                : normalizedReview.comment}
                        </p>
                        {isLongReview && (
                            <button
                                onClick={() => setShowFullReview(!showFullReview)}
                                className="font-medium mt-2 self-start transition-colors duration-200"
                                style={{
                                    color: styleConfig?.accentColor || '#4A90E2',
                                    fontSize: `${Math.max(textSize - 2, 12)}px`
                                }}
                            >
                                {showFullReview ? 'Read less' : 'Read more'}
                            </button>
                        )}
                    </>
                ) : (
                    <p
                        className="italic flex-1"
                        style={{
                            color: dateColor,
                            fontSize: `${textSize}px`
                        }}
                    >
                        No comment provided
                    </p>
                )}
            </div>
        </div>
    );
};

// Main Carousel Widget Preview
export const CarouselWidgetPreview = ({ reviews = [], loading = false, previewMode = 'desktop', selectedPlatforms = [], externalPlatformData = [], locationData = [], layout = 'carousel', widgetConfig = {} }) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [activeTab, setActiveTab] = useState('all');
    const [showPlatformModal, setShowPlatformModal] = useState(false);
    const [showReviewsModal, setShowReviewsModal] = useState(false);
    const [visibleReviews, setVisibleReviews] = useState(8); // For grid layout load more
    const reviewsPerPage = previewMode === 'mobile' ? 2 : 4;

    // Use passed reviews or fallback to sample data, but prioritize platform-specific data
    const displayReviews = (reviews.length > 0 && selectedPlatforms.length > 0) ? reviews : enhancedSampleReviews;

    // Extract unique platforms with their icon URLs for badge layout
    const platformsMap = new Map();
    displayReviews.forEach(review => {
        const platformName = review.platform?.name || review.platform;
        const iconUrl = review.platform?.iconLink;
        if (platformName && !platformsMap.has(platformName)) {
            platformsMap.set(platformName, { name: platformName, iconUrl });
        }
    });
    const uniquePlatforms = Array.from(platformsMap.values()).slice(0, 3);

    // Header configuration with defaults (all enabled by default)
    const headerConfig = widgetConfig?.headerConfig || {
        showHeader: true,
        elements: {
            heading: true,
            rating: true,
            numberOfReviews: true,
            writeReviewButton: true
        }
    };

    const tabsConfig = widgetConfig?.tabsConfig || {
        showTabs: true,
        elements: {
            icon: true,
            name: true,
            rating: true
        }
    };

    const widgetTitleConfig = widgetConfig?.widgetTitleConfig || {
        showTitle: false,
        title: "",
        caption: ""
    };

    // Style configuration with defaults
    const styleConfig = widgetConfig?.style || {};
    const colorScheme = styleConfig.colorScheme || 'light';

    // Define color scheme styles
    const themeStyles = {
        light: {
            container: 'bg-white',
            background: styleConfig.background?.color || '#FFFFFF',
            titleColor: styleConfig.widgetTitle?.titleColor || '#000000',
            captionColor: styleConfig.widgetTitle?.captionColor || '#666666',
            headerBg: styleConfig.header?.backgroundColor || '#FFFFFF',
            headerText: styleConfig.header?.textColor || '#000000',
            reviewBg: styleConfig.review?.backgroundColor || '#FFFFFF',
            reviewText: styleConfig.review?.textColor || '#000000',
            reviewerName: styleConfig.review?.reviewerNameColor || '#000000',
            dateColor: styleConfig.review?.dateColor || '#666666',
            sourceIconBg: styleConfig.review?.sourceIconBackgroundColor || '#FFFFFF'
        },
        dark: {
            container: 'bg-gray-800',
            background: styleConfig.background?.color || '#1F2937',
            titleColor: styleConfig.widgetTitle?.titleColor || '#FFFFFF',
            captionColor: styleConfig.widgetTitle?.captionColor || '#D1D5DB',
            headerBg: styleConfig.header?.backgroundColor || '#1F2937',
            headerText: styleConfig.header?.textColor || '#FFFFFF',
            reviewBg: styleConfig.review?.backgroundColor || '#374151',
            reviewText: styleConfig.review?.textColor || '#FFFFFF',
            reviewerName: styleConfig.review?.reviewerNameColor || '#FFFFFF',
            dateColor: styleConfig.review?.dateColor || '#D1D5DB',
            sourceIconBg: styleConfig.review?.sourceIconBackgroundColor || '#4B5563'
        }
    };

    const currentTheme = themeStyles[colorScheme];

    // Generate container styles based on widgetConfig
    const getContainerStyles = () => {
        const backgroundConfig = styleConfig.background || {};
        const width = backgroundConfig.width || 1200;
        const borderRadius = backgroundConfig.borderRadius ?? 16;
        const shadow = backgroundConfig.shadow ?? true;

        return {
            width: `${width}px`,
            maxWidth: '100%',
            borderRadius: `${borderRadius}px`,
            boxShadow: shadow ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' : 'none',
            fontFamily: getFontFamily(styleConfig.fontFamily || 'default')
        };
    };

    // Create platform data based on actual reviews with icon URLs
    const createPlatformData = () => {
        const platformStats = {};
        let totalRating = 0;
        let totalCount = 0;

        displayReviews.forEach(review => {
            const normalized = normalizeReviewData(review);
            const platformName = normalized.platform;
            const iconUrl = review.platform?.iconLink; // Get iconUrl from original review data

            if (!platformStats[platformName]) {
                platformStats[platformName] = { ratings: [], count: 0, iconUrl: iconUrl };
            }

            // If we don't have an iconUrl yet but this review has one, use it
            if (!platformStats[platformName].iconUrl && iconUrl) {
                platformStats[platformName].iconUrl = iconUrl;
            }

            platformStats[platformName].ratings.push(normalized.rating);
            platformStats[platformName].count++;
            totalRating += normalized.rating;
            totalCount++;
        });

        const platforms = [
            {
                id: 'all',
                name: 'All Reviews',
                rating: totalCount > 0 ? (totalRating / totalCount) : 0,
                count: totalCount,
                iconUrl: null // No specific icon for "All Reviews"
            }
        ];

        Object.entries(platformStats).forEach(([platform, stats]) => {
            const avgRating = stats.ratings.reduce((sum, rating) => sum + rating, 0) / stats.ratings.length;
            platforms.push({
                id: platform,
                name: platform.charAt(0).toUpperCase() + platform.slice(1),
                rating: avgRating,
                count: stats.count,
                iconUrl: stats.iconUrl // Include the iconUrl from review data
            });
        });

        return platforms;
    };

    const platformData = createPlatformData();

    const filteredReviews = activeTab === 'all'
        ? displayReviews
        : displayReviews.filter(review => {
            const normalized = normalizeReviewData(review);
            return normalized.platform.toLowerCase() === activeTab.toLowerCase();
        });

    const totalPages = Math.ceil(filteredReviews.length / reviewsPerPage);

    useEffect(() => {
        // Only auto-advance for carousel layout
        if (layout === 'carousel') {
            const interval = setInterval(() => {
                setCurrentPage((prev) => (prev + 1) % totalPages);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [totalPages, layout]);

    // Reset visible reviews when layout changes
    useEffect(() => {
        if (layout === 'grid') {
            setVisibleReviews(8);
        }
    }, [layout]);

    const getCurrentReviews = () => {
        const filteredData = activeTab === 'all' ? displayReviews : displayReviews.filter(review => {
            const normalized = normalizeReviewData(review);
            return normalized.platform === activeTab;
        });

        // For grid layout, return all visible reviews, for others use pagination
        if (layout === 'grid') {
            return filteredData.slice(0, visibleReviews);
        }

        const start = currentPage * reviewsPerPage;
        return filteredData.slice(start, start + reviewsPerPage);
    };

    const nextPage = () => {
        setCurrentPage((prev) => (prev + 1) % totalPages);
    };

    const prevPage = () => {
        setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
    };

    // Show loading state
    if (loading) {
        return (
            <div className="bg-gray-50 p-8 rounded-lg">
                <ReviewsLoadingState count={4} />
            </div>
        );
    }

    // Show empty state if no reviews
    if (displayReviews.length === 0) {
        return (
            <div className="bg-gray-50 p-8 rounded-lg">
                <ReviewsEmptyState message="No reviews to display. Add some reviews to see them here." />
            </div>
        );
    }

    const overallRating = platformData.find(p => p.id === 'all');

    // Helper function to get platform URL from location data
    const getPlatformUrl = (platformId) => {
        if (!locationData || !Array.isArray(locationData)) return null;

        for (const location of locationData) {
            const platform = location.platforms?.find(p => p.name === platformId);
            if (platform && platform.profileUrl) {
                return platform.profileUrl;
            }
        }
        return null;
    };

    return (
        <div
            className={`mx-auto ${colorScheme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}
            style={{
                backgroundColor: currentTheme.background,
                ...getContainerStyles()
            }}
        >
            {/* Widget Title Section - Hidden for Badge and Floating Badge Layouts */}
            {layout !== 'badge' && layout !== 'floating-badge' && widgetTitleConfig.showTitle && (
                <div className={`${previewMode === 'mobile' ? 'mb-4 p-4' : 'mb-6 p-6'}`}>
                    <h1
                        className={`${previewMode === 'mobile' ? 'mb-2' : 'mb-3'}`}
                        style={{
                            color: currentTheme.titleColor,
                            fontSize: `${styleConfig?.widgetTitle?.titleSize || 24}px`,
                            fontWeight: styleConfig?.widgetTitle?.titleBold ? '700' : 'normal',
                            fontStyle: styleConfig?.widgetTitle?.titleItalic ? 'italic' : 'normal',
                            textAlign: styleConfig?.widgetTitle?.alignment || 'center'
                        }}
                    >
                        {widgetTitleConfig.title}
                    </h1>
                    {widgetTitleConfig.caption && (
                        <p
                            style={{
                                color: currentTheme.captionColor,
                                fontSize: `${styleConfig?.widgetTitle?.captionSize || 15}px`,
                                fontWeight: styleConfig?.widgetTitle?.captionBold ? '700' : 'normal',
                                fontStyle: styleConfig?.widgetTitle?.captionItalic ? 'italic' : 'normal',
                                textAlign: styleConfig?.widgetTitle?.alignment || 'center'
                            }}
                        >
                            {widgetTitleConfig.caption}
                        </p>
                    )}
                </div>
            )}

            {/* Header Section - Hidden for Badge and Floating Badge Layouts */}
            {layout !== 'badge' && layout !== 'floating-badge' && headerConfig.showHeader && (
                <div className={`text-center ${previewMode === 'mobile' ? 'mb-6' : 'mb-8'}`}>
                    {/* Platform Tabs */}
                    {tabsConfig.showTabs && (
                        <div className={`flex flex-wrap justify-center gap-2 ${previewMode === 'mobile' ? 'mb-4' : 'mb-6'}`}>
                            {platformData.slice(0, previewMode === 'mobile' ? 3 : platformData.length).map((platform) => (
                                <button
                                    key={platform.id}
                                    onClick={() => {
                                        setActiveTab(platform.id);
                                        setCurrentPage(0);
                                        if (layout === 'grid') {
                                            setVisibleReviews(8); // Reset visible reviews for grid
                                        }
                                    }}
                                    className={`flex items-center space-x-2 rounded-md border transition-all duration-200 ${previewMode === 'mobile' ? 'px-3 py-2' : 'px-5 py-3'} ${activeTab === platform.id
                                        ? (colorScheme === 'dark' ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-900 text-white border-gray-900')
                                        : (colorScheme === 'dark' ? 'bg-gray-700 text-gray-300 border-gray-600 hover:border-gray-500 hover:bg-gray-600' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400 hover:bg-gray-50')
                                        }`}
                                >
                                    {platform.id !== 'all' && tabsConfig.elements.icon && (
                                        <PlatformIcon
                                            platform={platform.id}
                                            iconUrl={platform.iconUrl}
                                            className={previewMode === 'mobile' ? 'w-4 h-4' : 'w-5 h-5'}
                                        />
                                    )}
                                    {tabsConfig.elements.name && (
                                        <span className={`font-medium ${previewMode === 'mobile' ? 'text-sm' : ''}`}>{capitalizePlatformName(platform.name)}</span>
                                    )}
                                    {tabsConfig.elements.rating && (
                                        <span className={`font-bold ${previewMode === 'mobile' ? 'text-sm' : ''}`}>{platform.rating.toFixed(1)}</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Overall Rating Section */}
                    {(headerConfig.elements.rating || headerConfig.elements.numberOfReviews || headerConfig.elements.writeReviewButton) && (
                        <div className={`mx-auto rounded-lg ${previewMode === 'mobile' ? 'max-w-sm p-4 mb-6 space-y-3' : 'max-w-2xl p-6 mb-8 flex items-center justify-between'}`} style={{ backgroundColor: colorScheme === 'dark' ? '#374151' : '#F9FAFB' }}>
                            {(headerConfig.elements.rating || headerConfig.elements.numberOfReviews) && (
                                <div className={`${previewMode === 'mobile' ? 'text-center' : 'flex items-center space-x-4'}`}>
                                    <div>
                                        <div className={previewMode === 'mobile' ? 'text-center' : 'text-left'}>
                                            {headerConfig.elements.heading && (
                                                <div className={`font-medium mb-1 ${previewMode === 'mobile' ? 'text-xs' : 'text-sm'}`} style={{ color: colorScheme === 'dark' ? '#D1D5DB' : '#6B7280' }}>Overall Rating</div>
                                            )}
                                            <div className={`flex items-center ${previewMode === 'mobile' ? 'justify-center space-x-2' : 'space-x-3'}`}>
                                                {headerConfig.elements.rating && (
                                                    <>
                                                        <span className={`font-bold ${previewMode === 'mobile' ? 'text-2xl' : 'text-3xl'}`} style={{ color: colorScheme === 'dark' ? '#FFFFFF' : '#111827' }}>{overallRating.rating.toFixed(1)}</span>
                                                        <div className="flex space-x-1">
                                                            {[1, 2, 3, 4, 5].map((star) => (
                                                                <svg
                                                                    key={star}
                                                                    className={`${previewMode === 'mobile' ? 'w-4 h-4' : 'w-6 h-6'} ${star <= Math.floor(overallRating.rating)
                                                                        ? 'fill-current'
                                                                        : star <= overallRating.rating
                                                                            ? 'fill-current'
                                                                            : 'text-gray-300'
                                                                        } fill-current`}
                                                                    style={{
                                                                        color: star <= overallRating.rating
                                                                            ? (widgetConfig.style?.rating?.starsColor || '#F1C40F')
                                                                            : '#d1d5db'
                                                                    }}
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                                                </svg>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                                {headerConfig.elements.numberOfReviews && (
                                                    <span className={`${previewMode === 'mobile' ? 'text-sm' : ''}`} style={{ color: colorScheme === 'dark' ? '#9CA3AF' : '#6B7280' }}>({overallRating.count.toLocaleString()})</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {headerConfig.elements.writeReviewButton && (
                                <button
                                    onClick={() => {
                                        if (selectedPlatforms && selectedPlatforms.length > 1) {
                                            setShowPlatformModal(true);
                                        } else if (selectedPlatforms && selectedPlatforms.length === 1) {
                                            // For single platform, open directly
                                            const platformId = selectedPlatforms[0];
                                            const url = getPlatformUrl(platformId);
                                            if (url) {
                                                window.open(url, '_blank');
                                            }
                                        }
                                    }}
                                    className={`text-white font-medium rounded-lg transition-colors duration-200 ${previewMode === 'mobile' ? 'px-4 py-2 text-sm w-full' : 'px-6 py-2'}`}
                                    style={{ backgroundColor: styleConfig?.accentColor || '#4A90E2' }}
                                >
                                    Write a review
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Review Cards Layout */}
            <div className={`relative mb-8 ${previewMode === 'mobile' ? 'px-4' : 'px-8'}`}>
                {/* Carousel Layout */}
                {layout === 'carousel' && (
                    <div className={`grid gap-4 ${previewMode === 'mobile' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4'}`}>
                        {getCurrentReviews().map((review, index) => (
                            <CompactReviewCard key={review.id || index} review={review} previewMode={previewMode} themeStyles={currentTheme} styleConfig={styleConfig} />
                        ))}
                    </div>
                )}

                {/* Grid Layout */}
                {layout === 'grid' && (
                    <div>
                        <div className={`grid gap-4 ${previewMode === 'mobile' ? 'grid-cols-1' : 'grid-cols-4'}`}>
                            {getCurrentReviews().map((review, index) => (
                                <CompactReviewCard key={review.id || index} review={review} previewMode={previewMode} themeStyles={currentTheme} styleConfig={styleConfig} />
                            ))}
                        </div>

                        {/* Load More Button for Grid */}
                        {(() => {
                            const filteredData = activeTab === 'all' ? displayReviews : displayReviews.filter(review => {
                                const normalized = normalizeReviewData(review);
                                return normalized.platform === activeTab;
                            });
                            return filteredData.length > visibleReviews && (
                                <div className="flex justify-center mt-10" style={{ gap: '12px' }}>
                                    <button
                                        onClick={() => setVisibleReviews(prev => prev + 8)}
                                        className="text-white font-medium rounded-lg transition-colors duration-200"
                                        style={{
                                            backgroundColor: styleConfig?.accentColor || '#4A90E2',
                                            marginTop: '40px',
                                            padding: '12px 32px',
                                            minHeight: '48px'
                                        }}
                                    >
                                        Load More Reviews
                                    </button>
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* List Layout */}
                {layout === 'list' && (
                    <div className="space-y-4">
                        {getCurrentReviews().map((review, index) => (
                            <CompactReviewCard key={review.id || index} review={review} previewMode={previewMode} themeStyles={currentTheme} styleConfig={styleConfig} />
                        ))}
                    </div>
                )}

                {/* Masonry Layout */}
                {layout === 'masonry' && (
                    <div className={`columns-1 gap-4 ${previewMode === 'mobile' ? '' : 'md:columns-2 xl:columns-3'}`}>
                        {getCurrentReviews().map((review, index) => (
                            <div key={review.id || index} className="break-inside-avoid mb-4">
                                <CompactReviewCard review={review} previewMode={previewMode} themeStyles={currentTheme} styleConfig={styleConfig} />
                            </div>
                        ))}
                    </div>
                )}

                {/* Badge Layout - Dynamic Design */}
                {layout === 'badge' && (() => {
                    // Calculate dynamic data based on selected platforms/reviews
                    const filteredReviews = selectedPlatforms.length > 0
                        ? displayReviews.filter(review => {
                            const platform = review.platform?.name || review.platform;
                            return selectedPlatforms.some(selectedPlatform =>
                                platform.toLowerCase().includes(selectedPlatform.toLowerCase()) ||
                                selectedPlatform.toLowerCase().includes(platform.toLowerCase())
                            );
                        })
                        : displayReviews;

                    const totalReviews = filteredReviews.length;
                    const averageRating = totalReviews > 0
                        ? filteredReviews.reduce((sum, review) => sum + (review.rating || 0), 0) / totalReviews
                        : 4.8;

                    const selectedPlatformNames = selectedPlatforms.length > 0 ? selectedPlatforms : uniquePlatforms;

                    return (
                        <div className="flex justify-center">
                            <div
                                className={`rounded-lg shadow-sm border text-center ${previewMode === 'mobile' ? 'p-4 max-w-xs' : 'p-6 max-w-sm'} ${colorScheme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}
                                style={{ backgroundColor: currentTheme.reviewBg }}
                            >
                                {/* Platform Icons at the top */}
                                <div className="flex justify-center items-center space-x-2 mb-4">
                                    {uniquePlatforms.slice(0, 3).map((platformData, index) => (
                                        <div key={index} className="relative">
                                            <PlatformIcon
                                                platform={platformData.name}
                                                iconUrl={platformData.iconUrl}
                                                className={previewMode === 'mobile' ? 'w-8 h-8' : 'w-10 h-10'}
                                            />
                                        </div>
                                    ))}
                                    {selectedPlatformNames.length > 3 && (
                                        <div className={`rounded-full flex items-center justify-center ${previewMode === 'mobile' ? 'w-8 h-8' : 'w-10 h-10'} ${colorScheme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`}>
                                            <span className={`text-xs font-medium ${colorScheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>...</span>
                                        </div>
                                    )}
                                </div>

                                {/* Overall Rating Heading */}
                                <h3
                                    className={`font-bold mb-3 ${previewMode === 'mobile' ? 'text-lg' : 'text-xl'}`}
                                    style={{ color: currentTheme.reviewerName }}
                                >
                                    Overall Rating
                                </h3>

                                {/* Large Rating Number - Dynamic */}
                                <div
                                    className={`font-bold mb-2 ${previewMode === 'mobile' ? 'text-3xl' : 'text-4xl'}`}
                                    style={{ color: currentTheme.reviewText }}
                                >
                                    {averageRating.toFixed(1)}
                                </div>

                                {/* Star Rating - Dynamic */}
                                <div className="flex justify-center mb-4">
                                    <StarRating
                                        rating={Math.round(averageRating)}
                                        size={previewMode === 'mobile' ? 'w-5 h-5' : 'w-6 h-6'}
                                        color={widgetConfig.style?.rating?.starsColor || '#F1C40F'}
                                    />
                                </div>

                                {/* Review Link - Dynamic Count */}
                                <button
                                    onClick={() => setShowReviewsModal(true)}
                                    className={`underline transition-colors duration-200 ${previewMode === 'mobile' ? 'text-sm' : 'text-base'}`}
                                    style={{ color: styleConfig?.accentColor || '#4A90E2' }}
                                >
                                    Read our reviews
                                </button>
                            </div>
                        </div>
                    );
                })()}

                {/* Floating Badge Layout - Same as Badge but positioned as overlay */}
                {layout === 'floating-badge' && (() => {
                    // Calculate dynamic data based on selected platforms/reviews
                    const filteredReviews = selectedPlatforms.length > 0
                        ? displayReviews.filter(review => {
                            const platform = review.platform?.name || review.platform;
                            return selectedPlatforms.some(selectedPlatform =>
                                platform.toLowerCase().includes(selectedPlatform.toLowerCase()) ||
                                selectedPlatform.toLowerCase().includes(platform.toLowerCase())
                            );
                        })
                        : displayReviews;

                    const totalReviews = filteredReviews.length;
                    const averageRating = totalReviews > 0
                        ? filteredReviews.reduce((sum, review) => sum + (review.rating || 0), 0) / totalReviews
                        : 4.8;
                    const selectedPlatformNames = selectedPlatforms.length > 0 ? selectedPlatforms : uniquePlatforms;

                    return (
                        <>
                            {/* Demo website content background */}
                            <div className="relative min-h-screen bg-gray-50">
                                <div className={`bg-white rounded-lg flex items-center justify-center ${previewMode === 'mobile' ? 'p-6 min-h-[400px]' : 'p-8 min-h-[500px]'} mx-4 my-8`}>
                                    <div className="text-center">
                                        <h2 className={`text-gray-700 font-semibold mb-4 ${previewMode === 'mobile' ? 'text-lg' : 'text-xl'}`}>
                                            Your Website Content
                                        </h2>
                                        <p className={`text-gray-500 ${previewMode === 'mobile' ? 'text-sm' : ''} max-w-md`}>
                                            This represents your website content. The floating badge will appear as an overlay on top of your actual website.
                                        </p>
                                    </div>
                                </div>

                                {/* Floating Badge - Exact same design as Badge layout */}
                                <div className={`fixed z-50 ${previewMode === 'mobile' ? 'bottom-4 left-4' : 'bottom-6 left-6'}`}>
                                    <div
                                        className={`rounded-lg shadow-xl border text-center transition-all duration-300 hover:shadow-2xl ${previewMode === 'mobile' ? 'p-4 max-w-xs' : 'p-6 max-w-sm'} ${colorScheme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}
                                        style={{ backgroundColor: currentTheme.reviewBg }}
                                    >
                                        {/* Platform Icons at the top */}
                                        <div className="flex justify-center items-center space-x-2 mb-4">
                                            {uniquePlatforms.slice(0, 3).map((platformData, index) => (
                                                <div key={index} className="relative">
                                                    <PlatformIcon
                                                        platform={platformData.name}
                                                        iconUrl={platformData.iconUrl}
                                                        className={previewMode === 'mobile' ? 'w-8 h-8' : 'w-10 h-10'}
                                                    />
                                                </div>
                                            ))}
                                            {selectedPlatformNames.length > 3 && (
                                                <div className={`rounded-full flex items-center justify-center ${previewMode === 'mobile' ? 'w-8 h-8' : 'w-10 h-10'} ${colorScheme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`}>
                                                    <span className={`text-xs font-medium ${colorScheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>...</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Overall Rating Heading */}
                                        <h3
                                            className={`font-bold mb-3 ${previewMode === 'mobile' ? 'text-lg' : 'text-xl'}`}
                                            style={{ color: currentTheme.reviewerName }}
                                        >
                                            Overall Rating
                                        </h3>

                                        {/* Large Rating Number - Dynamic */}
                                        <div
                                            className={`font-bold mb-2 ${previewMode === 'mobile' ? 'text-3xl' : 'text-4xl'}`}
                                            style={{ color: currentTheme.reviewText }}
                                        >
                                            {averageRating.toFixed(1)}
                                        </div>

                                        {/* Star Rating - Dynamic */}
                                        <div className="flex justify-center mb-4">
                                            <StarRating
                                                rating={Math.round(averageRating)}
                                                size={previewMode === 'mobile' ? 'w-5 h-5' : 'w-6 h-6'}
                                                color={widgetConfig.style?.rating?.starsColor || '#F1C40F'}
                                            />
                                        </div>

                                        {/* Review Link - Dynamic Count */}
                                        <button
                                            onClick={() => setShowReviewsModal(true)}
                                            className={`underline transition-colors duration-200 ${previewMode === 'mobile' ? 'text-sm' : 'text-base'}`}
                                            style={{ color: styleConfig?.accentColor || '#4A90E2' }}
                                        >
                                            Read our reviews
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    );
                })()}

                {/* Slider Layout */}
                {layout === 'slider' && (
                    <div className="relative">
                        <div className={`overflow-x-auto ${previewMode === 'mobile' ? 'pb-4' : 'pb-6'}`}>
                            <div className="flex space-x-4 w-max">
                                {getCurrentReviews().map((review, index) => (
                                    <div key={review.id || index} className={`flex-shrink-0 ${previewMode === 'mobile' ? 'w-80' : 'w-96'}`}>
                                        <CompactReviewCard review={review} previewMode={previewMode} themeStyles={currentTheme} styleConfig={styleConfig} />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none"></div>
                    </div>
                )}

                {/* Navigation Controls - Only for Carousel */}
                {layout === 'carousel' && totalPages > 1 && (
                    <>
                        <NavigationArrow
                            direction="left"
                            onClick={prevPage}
                            disabled={currentPage === 0}
                        />
                        <NavigationArrow
                            direction="right"
                            onClick={nextPage}
                            disabled={currentPage === totalPages - 1}
                        />
                    </>
                )}
            </div>

            {/* Dot Indicators - Only for Carousel */}
            {layout === 'carousel' && totalPages > 1 && (
                <div className="flex justify-center mt-6 py-5 px-6 mb-12">
                    <div className="flex space-x-4">
                        {Array.from({ length: Math.min(3, totalPages) }, (_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentPage(index)}
                                className="relative w-3 h-3 rounded-full transition-all duration-200"
                                style={{
                                    minWidth: '15px',
                                    minHeight: '15px',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: index === currentPage
                                        ? (styleConfig.pagination?.color || '#4A90E2')
                                        : '#D1D5DB'
                                }}
                                onMouseOver={(e) => {
                                    if (index !== currentPage) {
                                        e.target.style.backgroundColor = '#9CA3AF';
                                    }
                                }}
                                onMouseOut={(e) => {
                                    if (index !== currentPage) {
                                        e.target.style.backgroundColor = '#D1D5DB';
                                    }
                                }}
                            >
                                <span
                                    className="w-3 h-3 rounded-full"
                                    style={{
                                        backgroundColor: index === currentPage
                                            ? (styleConfig.pagination?.color || '#4A90E2')
                                            : '#D1D5DB'
                                    }}
                                ></span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Platform Selection Modal */}
            <PlatformSelectionModal
                show={showPlatformModal}
                onClose={() => setShowPlatformModal(false)}
                selectedPlatforms={selectedPlatforms}
                platformData={externalPlatformData}
                locationData={locationData}
                onPlatformSelect={(_platformId, _url) => {
                    // Platform selection handled by modal
                }}
            />

            {/* Reviews Modal for Badge Layout */}
            {layout === 'badge' && (() => {
                // Calculate dynamic data for modal
                const filteredReviews = selectedPlatforms.length > 0
                    ? displayReviews.filter(review => {
                        const platform = review.platform?.name || review.platform;
                        return selectedPlatforms.some(selectedPlatform =>
                            platform.toLowerCase().includes(selectedPlatform.toLowerCase()) ||
                            selectedPlatform.toLowerCase().includes(platform.toLowerCase())
                        );
                    })
                    : displayReviews;

                const totalReviews = filteredReviews.length;
                const averageRating = totalReviews > 0
                    ? filteredReviews.reduce((sum, review) => sum + (review.rating || 0), 0) / totalReviews
                    : 4.8;

                const selectedPlatformNames = selectedPlatforms.length > 0 ? selectedPlatforms : uniquePlatforms;

                return (
                    <ReviewsModal
                        isOpen={showReviewsModal}
                        onClose={() => setShowReviewsModal(false)}
                        reviews={filteredReviews}
                        averageRating={averageRating}
                        totalReviews={totalReviews}
                        uniquePlatforms={selectedPlatformNames}
                        locationData={locationData}
                        accentColor={styleConfig?.accentColor || '#4A90E2'}
                    />
                );
            })()}

            {/* Reviews Modal for Floating Badge Layout */}
            {layout === 'floating-badge' && (() => {
                // Calculate dynamic data for modal
                const filteredReviews = selectedPlatforms.length > 0
                    ? displayReviews.filter(review => {
                        const platform = review.platform?.name || review.platform;
                        return selectedPlatforms.some(selectedPlatform =>
                            platform.toLowerCase().includes(selectedPlatform.toLowerCase()) ||
                            selectedPlatform.toLowerCase().includes(platform.toLowerCase())
                        );
                    })
                    : displayReviews;

                const totalReviews = filteredReviews.length;
                const averageRating = totalReviews > 0
                    ? filteredReviews.reduce((sum, review) => sum + (review.rating || 0), 0) / totalReviews
                    : 4.8;

                const selectedPlatformNames = selectedPlatforms.length > 0 ? selectedPlatforms : uniquePlatforms;

                return (
                    <ReviewsModal
                        isOpen={showReviewsModal}
                        onClose={() => setShowReviewsModal(false)}
                        reviews={filteredReviews}
                        averageRating={averageRating}
                        totalReviews={totalReviews}
                        uniquePlatforms={selectedPlatformNames}
                        locationData={locationData}
                        accentColor={styleConfig?.accentColor || '#4A90E2'}
                    />
                );
            })()}
        </div>
    );
};

// Simple Carousel Preview (for basic template)
export const SimpleCarouselPreview = ({ reviews = [], previewMode = 'desktop', selectedPlatforms = [] }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const displayReviews = (reviews.length > 0 && selectedPlatforms.length > 0)
        ? reviews.slice(0, 6) // Show more reviews for better preview
        : sampleReviews.slice(0, 3);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % displayReviews.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [displayReviews.length]);

    if (displayReviews.length === 0) {
        return (
            <div className="bg-gray-50 p-8 rounded-lg">
                <ReviewsEmptyState />
            </div>
        );
    }

    return (
        <div className={`bg-white rounded-lg shadow-sm border max-w-2xl mx-auto ${previewMode === 'mobile' ? 'p-4' : 'p-8'}`}>

            <div className="relative overflow-hidden h-48">
                {displayReviews.map((review, index) => {
                    const normalized = normalizeReviewData(review);
                    return (
                        <div
                            key={review.id || index}
                            className={`absolute inset-0 transition-transform duration-500 ${index === currentIndex ? 'translate-x-0' :
                                index < currentIndex ? '-translate-x-full' : 'translate-x-full'
                                }`}
                        >
                            <div className="text-center">
                                <StarRating
                                    rating={normalized.rating}
                                    size="w-6 h-6"
                                    color="#F1C40F"
                                />
                                <p className="text-gray-700 mt-4 mb-6 leading-relaxed">
                                    {normalized.comment}
                                </p>
                                <div className="flex items-center justify-center space-x-2">
                                    <div className="w-8 h-8 rounded-full relative">
                                        {isValidImageUrl(normalized.avatar) ? (
                                            <>
                                                <img
                                                    src={normalized.avatar}
                                                    alt={normalized.name}
                                                    className="w-8 h-8 rounded-full object-cover"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextElementSibling.style.display = 'flex';
                                                    }}
                                                />
                                                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium absolute top-0 left-0 hidden">
                                                    {generateInitials(normalized.name)}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                                                {normalized.avatar && !isValidImageUrl(normalized.avatar) ? normalized.avatar : generateInitials(normalized.name)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        <span className="font-medium text-gray-900">{normalized.name}</span>
                                        {normalized.isVerified && (
                                            <VerificationIcon size="14" accentColor="#4A90E2" />
                                        )}
                                    </div>
                                    <PlatformIcon platform={normalized.platform} className="w-4 h-4" />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex justify-center py-5 px-6 mt-6 mb-4">
                <div className="flex space-x-4">
                    {displayReviews.slice(0, Math.min(3, displayReviews.length)).map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentIndex(index)}
                            className="relative transition-colors"
                            style={{
                                minWidth: '44px',
                                minHeight: '44px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <span
                                className="w-3 h-3 rounded-full transition-colors"
                                style={{
                                    backgroundColor: index === currentIndex
                                        ? '#4A90E2' // Using default color for SimpleCarousel since it doesn't get styleConfig
                                        : '#D1D5DB'
                                }}
                            ></span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Reviews Carousel Preview (enhanced version)
export const ReviewsCarouselPreview = ({ reviews = [], loading = false, previewMode = 'desktop', selectedPlatforms = [] }) => {
    const [activeSlide, setActiveSlide] = useState(0);
    const displayReviews = (reviews.length > 0 && selectedPlatforms.length > 0)
        ? reviews.slice(0, 8) // Show more reviews for better preview
        : enhancedSampleReviews.slice(0, 5);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveSlide((prev) => (prev + 1) % displayReviews.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [displayReviews.length]);

    if (loading) {
        return (
            <div className="bg-gray-50 p-8 rounded-lg">
                <ReviewsLoadingState count={1} />
            </div>
        );
    }

    if (displayReviews.length === 0) {
        return (
            <div className="bg-gray-50 p-8 rounded-lg">
                <ReviewsEmptyState />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg border overflow-hidden">
            <div className={`bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center ${previewMode === 'mobile' ? 'p-4' : 'p-6'}`}>
                <h2 className={`font-bold mb-2 ${previewMode === 'mobile' ? 'text-xl' : 'text-2xl'}`}>Customer Testimonials</h2>
                <p className={`text-blue-100 ${previewMode === 'mobile' ? 'text-sm' : ''}`}>Real feedback from our valued customers</p>
            </div>

            <div className={previewMode === 'mobile' ? 'p-4' : 'p-8'}>
                <div className="relative">
                    {displayReviews.map((review, index) => {
                        const normalized = normalizeReviewData(review);
                        return (
                            <div
                                key={review.id || index}
                                className={`${index === activeSlide ? 'block' : 'hidden'} text-center`}
                            >
                                <div className="mb-6">
                                    <StarRating
                                        rating={normalized.rating}
                                        size="w-8 h-8"
                                        color="#F1C40F"
                                    />
                                </div>

                                <blockquote className="text-xl text-gray-700 mb-6 leading-relaxed">
                                    {normalized.comment}
                                </blockquote>

                                <div className="flex items-center justify-center space-x-4">
                                    <div className="w-16 h-16 rounded-full relative">
                                        {isValidImageUrl(normalized.avatar) ? (
                                            <>
                                                <img
                                                    src={normalized.avatar}
                                                    alt={normalized.name}
                                                    className="w-16 h-16 rounded-full object-cover"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextElementSibling.style.display = 'flex';
                                                    }}
                                                />
                                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 text-white rounded-full flex items-center justify-center text-lg font-bold absolute top-0 left-0 hidden">
                                                    {generateInitials(normalized.name)}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 text-white rounded-full flex items-center justify-center text-lg font-bold">
                                                {normalized.avatar && !isValidImageUrl(normalized.avatar) ? normalized.avatar : generateInitials(normalized.name)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-left">
                                        <div className="flex items-center space-x-1">
                                            <p className="font-semibold text-gray-900">{normalized.name}</p>
                                            {normalized.isVerified && (
                                                <VerificationIcon size="14" accentColor="#4A90E2" />
                                            )}
                                        </div>
                                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                                            <PlatformIcon platform={normalized.platform} className="w-4 h-4" />
                                            <span>{normalized.date}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex justify-center py-5 px-6 mt-8 mb-4">
                    <div className="flex space-x-4">
                        {displayReviews.slice(0, Math.min(3, displayReviews.length)).map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setActiveSlide(index)}
                                className="relative transition-colors"
                                style={{
                                    minWidth: '44px',
                                    minHeight: '44px',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <span className={`w-3 h-3 rounded-full transition-colors ${index === activeSlide ? 'bg-blue-600' : 'bg-gray-300'
                                    }`}></span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}; 